import type { JournalLine } from '../types';

/**
 * The arithmetic behind the journal editor's live debit / credit / difference
 * readout (CONVERSIONS_SPEC.md §5.5).
 *
 * <p>Extracted from the page on purpose: this is the logic the whole feature rests
 * on — a bookkeeper decides whether a journal is ready to export by reading these
 * three numbers — and it should be inspectable and testable without mounting a
 * React tree.</p>
 *
 * <p>Covered by `journalMath.test.ts`. Its balance arithmetic is pinned to the
 * SERVER's rule (`ManualJournalValidator.TotalDebit/TotalCredit` and the balance
 * comparison) through a shared fixture both suites read —
 * `__fixtures__/journal-balance-cases.json`. Change the arithmetic here and the C#
 * suite goes red too; that is the point.</p>
 *
 * ⚠ **Amounts above two decimal places are outside the agreement.** This module
 * rounds each line to cents and then sums; the server sums decimals and rounds once.
 * Those orders differ on sub-cent input. It does not matter in production only
 * because `ManualJournalValidator` rejects a third decimal place before any balance
 * comparison happens — so that rule is load-bearing for this file's correctness.
 */

/**
 * Amounts are held as strings while editing. Parsing on every keystroke would fight
 * the user mid-entry — `"1."` is not a number yet, and coercing it to `1` moves the
 * caret — so the model's number is derived at the boundary instead.
 *
 * Returns null for blank or unparseable input rather than 0: a blank amount and a
 * zero amount mean different things to the validator.
 */
export function parseAmount(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/**
 * Sums money in integer cents.
 *
 * Adding the doubles directly is how `0.1 + 0.2 !== 0.3` reaches a ledger: a journal
 * that is balanced to the cent can render a difference of `-0.00000000001`, which
 * compares unequal to zero and would leave the export button disabled on a perfectly
 * good journal. The server rounds to the cent before comparing; so does this.
 */
export function sumCents(values: (number | null | undefined)[]): number {
  const cents = values.reduce<number>((acc, v) => acc + Math.round((v ?? 0) * 100), 0);
  return cents / 100;
}

export interface JournalTotals {
  totalDebit: number;
  totalCredit: number;
  /** `totalDebit − totalCredit`, rounded to the cent. Zero means balanced. */
  difference: number;
  isBalanced: boolean;
  /** False for an untouched form, so a blank journal is not labelled "balanced". */
  hasAmounts: boolean;
}

export function computeTotals(lines: JournalLine[]): JournalTotals {
  const totalDebit = sumCents(lines.map((l) => l.debit));
  const totalCredit = sumCents(lines.map((l) => l.credit));
  const difference = Math.round((totalDebit - totalCredit) * 100) / 100;

  return {
    totalDebit,
    totalCredit,
    difference,
    isBalanced: difference === 0,
    hasAmounts: totalDebit !== 0 || totalCredit !== 0,
  };
}

export function formatMoney(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
